import { useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AFRICAN_COUNTRIES, AFRICAN_DIAL_CODES } from '../../../shared/constants/africa';
import { ensureLanguageLoaded, staticLanguageOptions } from '../../../shared/i18n';
import { useShop } from '../../../shared/store';
import type { SupportedLanguageCode } from '../../../shared/types';

interface ProfileFormState {
  name: string;
  email: string;
  phoneCode: string;
  phoneNumber: string;
  country: string;
  dateOfBirth: string;
  preferredLanguage: SupportedLanguageCode;
  profileImageUrl: string;
}

const MAX_PROFILE_IMAGE_SIZE_BYTES = 2 * 1024 * 1024;

const parsePhone = (phone?: string): { phoneCode: string; phoneNumber: string } => {
  const value = phone?.trim() ?? '';
  if (!value) {
    return { phoneCode: '+250', phoneNumber: '' };
  }

  const matched = value.match(/^(\+\d+)\s*(.*)$/);
  if (matched) {
    return {
      phoneCode: matched[1],
      phoneNumber: matched[2] ?? '',
    };
  }

  return { phoneCode: '+250', phoneNumber: value };
};

const ProfilePage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { currentUser, updateProfileAsync, isLoading } = useShop();
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const initialValues = useMemo<ProfileFormState>(() => {
    const phone = parsePhone(currentUser?.phone);
    return {
      name: currentUser?.name ?? '',
      email: currentUser?.email ?? '',
      phoneCode: phone.phoneCode,
      phoneNumber: phone.phoneNumber,
      country: currentUser?.country ?? 'Rwanda',
      dateOfBirth: currentUser?.dateOfBirth ?? '',
      preferredLanguage: (currentUser?.preferredLanguage ?? 'en') as SupportedLanguageCode,
      profileImageUrl: currentUser?.profileImageUrl ?? '',
    };
  }, [currentUser]);

  const [formState, setFormState] = useState<ProfileFormState>(initialValues);

  useEffect(() => {
    setFormState(initialValues);
  }, [initialValues]);

  if (!currentUser) {
    return null;
  }

  const handleFieldChange =
    (fieldName: keyof ProfileFormState) =>
    (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setFormState((previousState) => ({ ...previousState, [fieldName]: event.target.value }));
      setSuccessMessage('');
      setErrorMessage('');
    };

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      setErrorMessage('Please choose a valid image file.');
      setSuccessMessage('');
      return;
    }

    if (file.size > MAX_PROFILE_IMAGE_SIZE_BYTES) {
      setErrorMessage('Profile picture must be 2MB or smaller.');
      setSuccessMessage('');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setFormState((previousState) => ({
        ...previousState,
        profileImageUrl: typeof reader.result === 'string' ? reader.result : previousState.profileImageUrl,
      }));
      setSuccessMessage('');
      setErrorMessage('');
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setSuccessMessage('');
    setErrorMessage('');

    const result = await updateProfileAsync({
      name: formState.name.trim(),
      phone: `${formState.phoneCode} ${formState.phoneNumber.trim()}`.trim(),
      country: formState.country,
      dateOfBirth: formState.dateOfBirth,
      preferredLanguage: formState.preferredLanguage,
      profileImageUrl: formState.profileImageUrl,
    });
    if (!result.success) {
      setErrorMessage(
        result.message || t('profile.updateFailed', { defaultValue: 'Failed to update profile. Please try again.' }),
      );
      return;
    }

    if ((i18n.resolvedLanguage ?? i18n.language) !== formState.preferredLanguage) {
      await ensureLanguageLoaded(formState.preferredLanguage);
      await i18n.changeLanguage(formState.preferredLanguage);
    }

    setSuccessMessage(t('profile.updateSuccess', { defaultValue: 'Profile updated successfully.' }));
  };

  const dashboardPath =
    currentUser.role === 'admin'
      ? '/admin/dashboard/overview'
      : currentUser.role === 'manager'
        ? '/admin/dashboard/pod'
      : currentUser.role === 'vendor'
        ? '/vendor/dashboard/overview'
        : '/dashboard/home';

  return (
    <section className="mx-auto w-full max-w-4xl space-y-6 px-4 pb-8 pt-24 sm:px-6 lg:px-8">
      <header>
        <h1 className="text-3xl font-semibold text-slate-900">{t('profile.title', { defaultValue: 'Profile' })}</h1>
        <p className="mt-1 text-sm text-slate-600">
          {t('profile.subtitle', {
            defaultValue: 'Update your personal information, language, and account settings.',
          })}
        </p>
      </header>

      <form onSubmit={handleSubmit} className="grid gap-4 rounded-3xl border border-orange-100 bg-white p-6 shadow-sm sm:grid-cols-2">
        <div className="sm:col-span-2 flex items-center gap-3">
          {formState.profileImageUrl ? (
            <img src={formState.profileImageUrl} alt={formState.name || currentUser.name} className="h-16 w-16 rounded-full object-cover" />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-orange-500 text-lg font-semibold text-white">
              {currentUser.name.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex cursor-pointer rounded-xl border border-orange-200 px-3 py-2 text-sm font-semibold text-orange-700">
              {t('profile.uploadPicture', { defaultValue: 'Upload picture' })}
              <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
            </label>
            {formState.profileImageUrl && (
              <button
                type="button"
                onClick={() => {
                  setFormState((previousState) => ({ ...previousState, profileImageUrl: '' }));
                  setSuccessMessage('');
                  setErrorMessage('');
                }}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300"
              >
                {t('profile.removePicture', { defaultValue: 'Remove picture' })}
              </button>
            )}
            <p className="w-full text-xs text-slate-500">
              {t('profile.pictureHint', { defaultValue: 'PNG/JPG up to 2MB.' })}
            </p>
          </div>
        </div>

        <label className="grid gap-1 text-sm font-medium text-slate-700">
          {t('profile.fields.name', { defaultValue: 'Name' })}
          <input
            type="text"
            value={formState.name}
            onChange={handleFieldChange('name')}
            className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 focus:border-orange-400 focus:outline-none"
          />
        </label>

        <label className="grid gap-1 text-sm font-medium text-slate-700">
          {t('profile.fields.email', { defaultValue: 'Email' })}
          <input
            type="email"
            value={formState.email}
            disabled
            className="rounded-xl border border-slate-200 bg-slate-100 px-3 py-2 text-slate-500"
          />
        </label>

        <label className="grid gap-1 text-sm font-medium text-slate-700">
          {t('profile.fields.phoneCode', { defaultValue: 'Phone Code' })}
          <select
            value={formState.phoneCode}
            onChange={handleFieldChange('phoneCode')}
            className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 focus:border-orange-400 focus:outline-none"
          >
            {AFRICAN_DIAL_CODES.map((dialCode) => (
              <option key={`${dialCode.country}-${dialCode.code}`} value={dialCode.code}>
                {dialCode.code}
              </option>
            ))}
          </select>
        </label>

        <label className="grid gap-1 text-sm font-medium text-slate-700">
          {t('profile.fields.phoneNumber', { defaultValue: 'Phone Number' })}
          <input
            type="tel"
            value={formState.phoneNumber}
            onChange={handleFieldChange('phoneNumber')}
            className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 focus:border-orange-400 focus:outline-none"
          />
        </label>

        <label className="grid gap-1 text-sm font-medium text-slate-700">
          {t('profile.fields.country', { defaultValue: 'Country' })}
          <select
            value={formState.country}
            onChange={handleFieldChange('country')}
            className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 focus:border-orange-400 focus:outline-none"
          >
            {AFRICAN_COUNTRIES.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>
        </label>

        <label className="grid gap-1 text-sm font-medium text-slate-700">
          Date of Birth
          <input
            type="date"
            value={formState.dateOfBirth}
            onChange={handleFieldChange('dateOfBirth')}
            className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 focus:border-orange-400 focus:outline-none"
          />
        </label>

        <label className="grid gap-1 text-sm font-medium text-slate-700 sm:col-span-2">
          Preferred Language
          <select
            value={formState.preferredLanguage}
            onChange={handleFieldChange('preferredLanguage')}
            className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 focus:border-orange-400 focus:outline-none"
          >
            {staticLanguageOptions.map((option) => (
              <option key={option.code} value={option.code}>
                {option.shortLabel}
              </option>
            ))}
          </select>
        </label>

        {errorMessage && <p className="sm:col-span-2 text-sm font-semibold text-red-600">{errorMessage}</p>}
        {successMessage && <p className="sm:col-span-2 text-sm font-semibold text-emerald-700">{successMessage}</p>}

        <div className="sm:col-span-2 flex flex-wrap gap-2">
          <button
            type="submit"
            disabled={isLoading}
            className="rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-600"
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            type="button"
            onClick={() => navigate(dashboardPath)}
            className="rounded-xl border border-orange-200 px-4 py-2 text-sm font-semibold text-orange-700 transition hover:border-orange-300"
          >
            Back to Dashboard
          </button>
        </div>
      </form>
      <p className="text-xs text-slate-500">{t('common.brand')} profile settings are synced with your account when the API is available.</p>
    </section>
  );
};

export default ProfilePage;
